const CJK_PATTERN = /[\u3400-\u9fff]/;
const NUMBER_PATTERN = /^\d+$/;

function normalizeText(value) {
    return String(value ?? "").trim().toLowerCase();
}

function getAirportName(airport) {
    return airport?.name_zh || airport?.chinese_name || airport?.name || airport?.airport_name || "";
}

function addAirportLookupKey(lookup, key, airport) {
    const normalizedKey = normalizeText(key);
    if (normalizedKey) {
        lookup.set(normalizedKey, airport);
    }
}

function buildAirportLookup(airportsMap = {}) {
    const lookup = new Map();

    Object.entries(airportsMap || {}).forEach(([key, airport]) => {
        addAirportLookupKey(lookup, key, airport);
        addAirportLookupKey(lookup, airport?.iata_code, airport);
        addAirportLookupKey(lookup, airport?.icao_code, airport);
        addAirportLookupKey(lookup, airport?.airport_code, airport);
        addAirportLookupKey(lookup, airport?.code, airport);
    });

    return lookup;
}

function getAirportCodes(code, airportLookup) {
    const normalizedCode = normalizeText(code);
    const airport = airportLookup.get(normalizedCode);

    return [
        normalizedCode,
        normalizeText(airport?.iata_code),
        normalizeText(airport?.icao_code),
        normalizeText(airport?.airport_code),
        normalizeText(airport?.code),
    ].filter(Boolean);
}

function getAirportNames(code, rawFlight, airportLookup, side) {
    const airport = airportLookup.get(normalizeText(code));
    const directNames = side === "dep"
        ? [rawFlight?.dep_airport_name, rawFlight?.departure_airport_name, rawFlight?.departureAirportName, rawFlight?.depAirportName]
        : [rawFlight?.arr_airport_name, rawFlight?.arrival_airport_name, rawFlight?.arrivalAirportName, rawFlight?.arrAirportName];

    return [getAirportName(airport), ...directNames].filter(Boolean);
}

function getFlightField(row, snakeKey, camelKey) {
    return row?.rawFlight?.[snakeKey] ?? row?.[camelKey];
}

function rowMatchesToken(row, token, airportLookup) {
    const normalizedToken = normalizeText(token);
    if (!normalizedToken) {
        return true;
    }

    const rawFlight = row?.rawFlight || row || {};
    const aircraftReg = normalizeText(getFlightField(row, "aircraft_reg", "aircraftReg"));
    const flightNumber = normalizeText(getFlightField(row, "flight_number", "flightNumber"));
    const departureAirport = getFlightField(row, "departure_airport", "departureAirport");
    const arrivalAirport = getFlightField(row, "arrival_airport", "arrivalAirport");
    const stcType = normalizeText(getFlightField(row, "stc_type", "stcType"));

    if (CJK_PATTERN.test(token)) {
        const airportNames = [
            ...getAirportNames(departureAirport, rawFlight, airportLookup, "dep"),
            ...getAirportNames(arrivalAirport, rawFlight, airportLookup, "arr"),
        ];

        return airportNames.some((name) => normalizeText(name).includes(normalizedToken));
    }

    if (NUMBER_PATTERN.test(normalizedToken)) {
        return flightNumber.includes(normalizedToken);
    }

    const airportCodes = [
        ...getAirportCodes(departureAirport, airportLookup),
        ...getAirportCodes(arrivalAirport, airportLookup),
    ];

    return aircraftReg === normalizedToken
        || airportCodes.includes(normalizedToken)
        || stcType === normalizedToken;
}

function isFilterableRow(row) {
    return row?._rowType === "flight" || row?._rowType === "change-preview" || !row?._rowType;
}

export function getTimetableQuickFilterTokens(query = "") {
    return String(query || "").trim().split(/\s+/).filter(Boolean);
}

export function filterTimetableRows(rows = [], query = "", airportsMap = {}) {
    const tokens = getTimetableQuickFilterTokens(query);
    if (!tokens.length) {
        return rows;
    }

    const airportLookup = buildAirportLookup(airportsMap);
    return rows.filter((row) => isFilterableRow(row) && tokens.every((token) => rowMatchesToken(row, token, airportLookup)));
}
